/**
 * Wizard application store — single source of truth for the Vesta-direct
 * wizard's `pathLoanId` and the Vesta child-object id map.
 *
 * OWNER DIRECTIVE — CREATE IS CALLED EXACTLY ONCE per application:
 * `POST /api/v1/application` may only run when NO loanId exists for the
 * in-progress application. This module is the structural enforcement point:
 *
 *  - `createApplicationOnce()` is the ONLY sanctioned path to
 *    `wizardService.create` (never call the service directly from UI code).
 *  - If a loanId is already known (from a previous create OR from resume),
 *    it throws `CreateOnceViolationError` WITHOUT any network call.
 *  - Concurrent double-invocations (double-click, effect re-run) are deduped
 *    to the single in-flight promise.
 *
 * The id map (borrower.id, coBorrower.id, incomes[].id, employer ids,
 * assets[].id, subjectProperty.id) is derived from the latest server
 * `LoanResponse` — NEVER from form state (spec §8.2: re-sending a child
 * without its Vesta id creates duplicates).
 */

import { wizardService } from '@/services/wizard';
import type { LoanResponse, NewLoanRequest, NewLoanResponse } from '@/types/wizard';

const STORAGE_KEY = 'path_wizard_loan_id';

export class CreateOnceViolationError extends Error {
  loanId: string;
  constructor(loanId: string) {
    super(
      `Application already exists (loanId=${loanId}) — POST /application must be called exactly once. ` +
        'Use PATCH /application/{loanId} for all subsequent saves.',
    );
    this.name = 'CreateOnceViolationError';
    this.loanId = loanId;
  }
}

export interface WizardIdMap {
  /** Vesta borrower id — echo as `borrower.id` on every PATCH. */
  borrowerId?: string;
  /** Vesta co-borrower id (when present). */
  coBorrowerId?: string;
  /** Vesta subject-property id. */
  subjectPropertyId?: string;
  /** Vesta income ids for the primary borrower (in server order). */
  borrowerIncomeIds: string[];
  /** Vesta income ids for the co-borrower. */
  coBorrowerIncomeIds: string[];
  /** Borrower income ids that carry an employer (employment rows, server order). */
  borrowerEmploymentIncomeIds: string[];
  /** Borrower income ids WITHOUT an employer (other-income rows, server order). */
  borrowerOtherIncomeIds: string[];
  /** Co-borrower employment income ids (server order). */
  coBorrowerEmploymentIncomeIds: string[];
  /** Co-borrower other-income ids (server order). */
  coBorrowerOtherIncomeIds: string[];
  /** incomeId → employerId for id echo on employer edits. */
  employerIdByIncomeId: Record<string, string>;
  /** Vesta asset ids (top-level `assets[]`, server order). */
  assetIds: string[];
  /** Vesta liability ids (top-level `liabilities[]`, server order). */
  liabilityIds: string[];
}

export interface WizardApplicationState {
  /** PATH DB row id (`pathLoanId`) — the ROUTE id for /application/{loanId}/*. */
  loanId?: string;
  /** Vesta loan guid (`LoanResponse.id`) — informational only. */
  vestaLoanId?: string;
  loanNumber?: string | number;
  ids: WizardIdMap;
}

function emptyIds(): WizardIdMap {
  return {
    borrowerIncomeIds: [],
    coBorrowerIncomeIds: [],
    borrowerEmploymentIncomeIds: [],
    borrowerOtherIncomeIds: [],
    coBorrowerEmploymentIncomeIds: [],
    coBorrowerOtherIncomeIds: [],
    employerIdByIncomeId: {},
    assetIds: [],
    liabilityIds: [],
  };
}

function readPersistedLoanId(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

let state: WizardApplicationState = {
  loanId: readPersistedLoanId(),
  ids: emptyIds(),
};

let inFlightCreate: Promise<NewLoanResponse> | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

function persistLoanId(loanId: string | undefined): void {
  try {
    if (typeof window === 'undefined') return;
    if (loanId) window.localStorage.setItem(STORAGE_KEY, loanId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable — in-memory state still works for the session
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getWizardApplication(): WizardApplicationState {
  return state;
}

/** For `useSyncExternalStore`. */
export function subscribeWizardApplication(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Register a known loanId (resume path: route param / incomplete-apps list).
 * After this, `createApplicationOnce` throws — resuming an application must
 * never create a second loan.
 */
export function setWizardLoanId(loanId: string): void {
  if (!loanId) return;
  if (state.loanId === loanId) return;
  state = { ...state, loanId, ids: state.loanId ? emptyIds() : state.ids };
  persistLoanId(loanId);
  notify();
}

/**
 * Absorb a server `LoanResponse` (create/GET/PATCH result) into the id map.
 * This is the ONLY place child ids come from — never form state.
 */
export function absorbLoanResponse(loanId: string, response: LoanResponse): void {
  // NP-557: the map is rebuilt from scratch on EVERY save response, so a response
  // that merely OMITS the co-borrower used to wipe `coBorrowerId` for the rest of
  // the session — and buildIncomeTabDelta only sends co-borrower incomes when that
  // id is known, so their employment was dropped from the PATCH with no error and
  // the step still reported success. An omission is not a statement that the
  // co-borrower is gone; keep what the loan already told us.
  const previous = state.loanId === loanId ? state.ids : emptyIds();
  const ids = emptyIds();
  const borrower = response.borrower;
  if (borrower?.id) ids.borrowerId = borrower.id;
  if (response.coBorrower?.id) ids.coBorrowerId = response.coBorrower.id;
  if (response.subjectProperty?.id) ids.subjectPropertyId = response.subjectProperty.id;

  for (const income of borrower?.incomes ?? []) {
    if (income?.id) {
      ids.borrowerIncomeIds.push(income.id);
      // Employment rows (have an employer) and other-income rows hydrate into
      // SEPARATE step-data lists — track their ids separately so the per-tab
      // delta builders can index-match each list against server order.
      if (income.employer?.name || income.employer?.id) {
        ids.borrowerEmploymentIncomeIds.push(income.id);
      } else {
        ids.borrowerOtherIncomeIds.push(income.id);
      }
      if (income.employer?.id) ids.employerIdByIncomeId[income.id] = income.employer.id;
    }
  }
  for (const income of response.coBorrower?.incomes ?? []) {
    if (income?.id) {
      ids.coBorrowerIncomeIds.push(income.id);
      if (income.employer?.name || income.employer?.id) {
        ids.coBorrowerEmploymentIncomeIds.push(income.id);
      } else {
        ids.coBorrowerOtherIncomeIds.push(income.id);
      }
      if (income.employer?.id) ids.employerIdByIncomeId[income.id] = income.employer.id;
    }
  }
  // Copilot: an explicit `coBorrower: null` is a statement that there is none —
  // only an ABSENT key means "this response isn't telling you either way".
  if (!('coBorrower' in response)) {
    ids.coBorrowerId = previous.coBorrowerId;
    ids.coBorrowerIncomeIds = previous.coBorrowerIncomeIds;
    ids.coBorrowerEmploymentIncomeIds = previous.coBorrowerEmploymentIncomeIds;
    ids.coBorrowerOtherIncomeIds = previous.coBorrowerOtherIncomeIds;
    // Their employer ids ride the same omission.
    ids.employerIdByIncomeId = { ...previous.employerIdByIncomeId, ...ids.employerIdByIncomeId };
  }

  for (const asset of response.assets ?? []) {
    if (asset?.id) ids.assetIds.push(asset.id);
  }
  for (const liability of response.liabilities ?? []) {
    if (liability?.id) ids.liabilityIds.push(liability.id);
  }

  state = {
    ...state,
    loanId,
    vestaLoanId: response.id ?? state.vestaLoanId,
    loanNumber: response.loanNumber ?? state.loanNumber,
    ids,
  };
  persistLoanId(loanId);
  notify();
}

/** Full reset — new application after submit, logout, or stale-loan cleanup. */
export function clearWizardApplication(): void {
  state = { loanId: undefined, vestaLoanId: undefined, loanNumber: undefined, ids: emptyIds() };
  inFlightCreate = null;
  persistLoanId(undefined);
  notify();
}

/** True when create is still allowed (no loanId known, nothing in flight). */
export function canCreateApplication(): boolean {
  return !state.loanId && !inFlightCreate;
}

/**
 * The single sanctioned create path — CREATE EXACTLY ONCE.
 *
 * - loanId already known → throws `CreateOnceViolationError` (no network call).
 * - create already in flight → returns the SAME promise (double-click safe).
 * - otherwise → POST /application, then store `pathLoanId` + child ids.
 *
 * @param createFn injectable for tests; defaults to `wizardService.create`.
 */
export function createApplicationOnce(
  body: NewLoanRequest,
  createFn: (b: NewLoanRequest) => Promise<NewLoanResponse> = wizardService.create,
): Promise<NewLoanResponse> {
  if (state.loanId) {
    return Promise.reject(new CreateOnceViolationError(state.loanId));
  }
  if (inFlightCreate) {
    return inFlightCreate;
  }

  inFlightCreate = (async () => {
    try {
      const response = await createFn(body);
      if (!response?.pathLoanId) {
        throw new Error('POST /application returned no pathLoanId — cannot route subsequent saves');
      }
      absorbLoanResponse(response.pathLoanId, response);
      return response;
    } finally {
      inFlightCreate = null;
    }
  })();

  return inFlightCreate;
}
